<template>
    <el-config-provider :locale="locale === 'cn' ? zhCn : en">
        <el-container>
            <el-aside v-if="!isHiddenNav">
                <img src="@/assets/images/expand.png" class="platform-icon" @click="handleCollapseClick">
                <el-menu
                    class="el-menu-vertical-demo"
                    :default-active="onRoutes"
                    :collapse="collapse"
                    mode="vertical"
                    router
                >
                    <div :class="['platform-title', collapse && 'is-collapse']">
                        <img
                            :class="['logo-text', collapse && 'logo-text-collapse']"
                            src="@/assets/icons/svg/salesGo.png"
                        >
                        <img class="logo" src="@/assets/icons/svg/single_logo.png">
                    </div>
                    <template v-for="item in routerList">
                        <template v-if="!item.children && !item.hidden">
                            <el-menu-item :key="item.name" :index="item.path">
                                <svg-icon class-name="menu-icon" :icon-class="item.icon" />
                                <template #title>
                                    &nbsp;&nbsp;{{ $t(item.name?.toString() || '') }}
                                </template>
                            </el-menu-item>
                        </template>
                        <template v-else>
                            <el-sub-menu v-if="!item.hidden" :key="item.name" :index="item.path">
                                <template #title>
                                    <svg-icon class-name="menu-icon" :icon-class="item.icon" />
                                    <span>&nbsp;&nbsp;{{ $t(item.name || '') }}</span>
                                </template>
                                <!-- 子菜单的path拼接了父级path -->
                                <div v-for="sub in item.children" :key="sub.name">
                                    <el-menu-item v-if="!sub.children" :index="`${item.path}/${sub.path}`">
                                        <template #title>
                                            &nbsp;&nbsp;{{ $t(sub.name) }}
                                        </template>
                                    </el-menu-item>
                                    <el-sub-menu v-else :index="`${item.path}/${sub.path}`" class="inside_sub-menu">
                                        <template #title>
                                            <span>&nbsp;&nbsp;{{ $t(sub.name) }}</span>
                                        </template>
                                        <!-- 子菜单的path拼接了父级path -->
                                        <div v-for="son in sub.children" :key="son.name">
                                            <el-menu-item
                                                v-if="!son.hidden"
                                                :index="`${item.path}/${sub.path}/${son.path}`"
                                            >
                                                <template #title>
                                                    &nbsp;&nbsp;{{ $t(son.name) }}
                                                </template>
                                            </el-menu-item>
                                        </div>
                                    </el-sub-menu>
                                </div>
                            </el-sub-menu>
                        </template>
                    </template>
                </el-menu>
            </el-aside>
            <el-container v-if="!isHiddenNav">
                <watermark
                    style="width: 100%; height: 100%"
                    :font="waterFont"
                    :width="360"
                    :height="128"
                    :rotate="-25"
                    :content="watermarkContent"
                >
                    <div class="el-container-page">
                        <el-header>
                            <el-breadcrumb :separator-icon="ArrowRight">
                                <el-breadcrumb-item
                                    v-for="(item, index) in matchedRouters"
                                    :key="index"
                                    :to="{path: item.path}"
                                >
                                    {{ $t(item.name?.toString() || '') }}
                                </el-breadcrumb-item>
                            </el-breadcrumb>
                            <div class="headers-right">
                                <task-notice />
                                <timezone-select />
                                <language-select />
                                <div v-if="userInfo && userInfo.base_info" class="user-info">
                                    <el-tooltip
                                        placement="bottom"
                                        :show-arrow="false"
                                        popper-class="tooltip-popper-style"
                                        effect="light"
                                    >
                                        <template #content>
                                            <div class="user-tooltip">
                                                <ul class="user-menus">
                                                    <li @click="showPersonCenter">
                                                        <svg-icon class="user-icon" icon-class="tablePerson" />
                                                        {{ $t('menu.userInfo') }}
                                                    </li>
                                                    <li @click="logout">
                                                        <svg-icon class="user-icon" icon-class="tableQuit" />
                                                        {{ $t('btn.logout') }}
                                                    </li>
                                                </ul>
                                            </div>
                                        </template>
                                        <user-avatar
                                            v-if="userInfo && userInfo.base_info.email"
                                            class="user-avatar"
                                            :name="userInfo.base_info.email"
                                        />
                                        <svg-icon v-else icon-class="user" />
                                    </el-tooltip>
                                </div>
                            </div>
                        </el-header>
                        <person-info v-model="personInfoVisible" :user-info="userInfo" />
                        <el-main :key="componentKey" v-loading="pageLoading">
                            <router-view v-if="userStore.hasPermissionView" />
                            <no-permisson v-else />
                        </el-main>
                    </div>
                </watermark>
            </el-container>
            <router-view v-else />
        </el-container>
    </el-config-provider>
</template>

<script setup lang="ts">
import {ref, reactive, computed, onBeforeMount, onMounted, watch} from 'vue';
import {RouterView, useRoute, useRouter} from 'vue-router';
import router, {routes} from '@/router';
import type {RouteItem, ChildrenItem} from '@/router';
import {ArrowRight} from '@element-plus/icons-vue';
import NoPermisson from '@/components/no-permission/index.vue';
import {defineUserStore, filterAsyncRoutes} from '@/stores/user';
// import {routesModule, filterAsyncRoutes} from '@/stores/permission';
import {getUserInfo, Logout} from '@/apis/user';
import {ElMessage} from 'element-plus';
import localStorage from './utils/localStorage';
import Watermark from '@/components/watermark/index.vue';
import LanguageSelect from '@/components/languageSelect/index.vue';
import timezoneSelect from '@/components/timezone-select/index.vue';
import UserAvatar from '@/components/avatar-box/index.vue';
import PersonInfo from '@/components/person-center/index.vue';
import TaskNotice from '@/components/task-notice/index.vue';
import zhCn from 'element-plus/es/locale/lang/zh-cn';
import en from 'element-plus/es/locale/lang/en';
import {useI18n} from 'vue-i18n';
import {useUnderlingAndDistrictCN} from '@/hooks/useUnderlingAndDistrictCN';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
dayjs.extend(utc);
dayjs.extend(timezone);

const {locale, t} = useI18n();
const userStore = defineUserStore();
const {fetchUnderList, fetchOrgList} = useUnderlingAndDistrictCN();
// const routeModule = routesModule();
const collapse = ref(false);
const route = useRoute();
const personInfoVisible = ref(false);

const waterFont = reactive({
    color: 'rgba(136, 136, 136, 0.15)',
    fontSize: 14,
    fontWeight: 'normal',
    fontStyle: 'normal',
    fontFamily: 'sans-serif',
    textAlign: 'center',
    textBaseline: 'top'
});
const onRoutes = computed(() => {
    return route.path;
});
//是否隐藏左侧边栏跟头部
const isHiddenNav = computed(() => {
    return route.meta.showNav;
});
// 用于面包屑
const Router = useRouter();
const matchedRouters = computed(() => {
    return Router.currentRoute.value.matched;
});
const pageLoading = ref(false);
// resizeChart
const handleCollapseClick = () => {
    collapse.value = !collapse.value;
};

// 获取用户信息
const userInfo = computed(() => {
    return userStore.User;
});
const roleIds = computed(() => {
    return userStore.User?.role_info?.role_id || '';
});
const watermarkContent = computed(() => {
    const userName = userInfo.value?.base_info?.user_name || '';
    return ['SalesGo ' + userName, dayjs(new Date()).format('YYYY-MM-DD')];
});
const componentKey = computed(() => {
    return userStore.componentKey;
});
const getUserInfos = async () => {
    pageLoading.value = true;
    try {
        const data = await getUserInfo();
        if (data.errno === 0) {
            const res = data.data || {};
            userStore.setUser(res);
            const roleId = res?.role_info?.role_id || '';
            roleId && userStore.setRoutes([roleId]);
        }
    } catch (error) {}
    pageLoading.value = false;
};

const showPersonCenter = () => {
    personInfoVisible.value = true;
};
const usersInfo = userInfo.value;
// 不存在，则直接请求userinfo
if (!(usersInfo && usersInfo.base_info)) {
    getUserInfos();
} else {
    // 用户信息已存在，直接设置路由
    const roleId = usersInfo?.role_info?.role_id || '';
    roleId && userStore.setRoutes([roleId]);
}
//退出登录
const result = ref<any>();
const logout = async () => {
    result.value = await Logout();
    if (result.value.errno === 0) {
        localStorage.remove('user');
        userStore.removeUser();
        // ElMessage.success('登出成功');
        window.location.href = result.value.data;
    } else {
        ElMessage.error(t('msg.workError'));
    }
};
const routerList = computed(() => {
    const arr = userStore.routerObj?.routes || [];
    return filterAsyncRoutes(arr, [roleIds.value]);
});

watch(() => locale.value, (val) => {
    fetchOrgList();
});

// 在应用入口文件中调用 useUnderList，确保在应用挂载之前进行初始化
onBeforeMount(() => {
    roleIds.value && roleIds.value !== '' && getUserInfos();
    fetchUnderList();
    fetchOrgList();
});

</script>
<style lang="scss">
.el-container-page {
    display: flex;
    flex-direction: column;
    height: 100vh;
    box-sizing: border-box;
    min-width: 0;
}
div {
    box-sizing: border-box;
}
.el-checkbox .el-checkbox__label {
    color: var(--el-text-color-primary);
}

// 日期时间选择器隐藏分
.date-time-picker-delete-minutes {
    .el-scrollbar:nth-of-type(2) {
        display: none !important;
    }
    .el-time-spinner {
        text-align: center;
    }
}
// 日期时间选择器隐藏秒
.date-time-picker-delete-second {
    .el-scrollbar:nth-of-type(3) {
        display: none !important;
    }
    .el-time-spinner {
        text-align: center;
    }
}
</style>
<style scoped lang="scss">
.el-container {
    height: 100vh;
}
.el-header {
    background: #fff;
    height: 56px;
    line-height: 56px;
    padding-left: 20px;
    color: #303133;
    font-weight: bold;
    font-size: 16px;
    display: flex;
    align-items: center;
    border-bottom: 1px solid rgba(194, 204, 217, 0.3);
    justify-content: space-between;
    .headers-right {
        height: 100%;
        display: flex;
        align-items: center;
        gap: 10px;
        font-size: 22px;
        svg {
            cursor: pointer;
        }
    }
    .user-info {
        // display: flex;
        // padding-top: 12px;
        font-size: 22px;
        svg {
            cursor: pointer;
            vertical-align: middle;
        }
        .user-avatar {
            box-shadow: 1px 2px 6px 0px rgba(0, 102, 255, 0.25);
            cursor: pointer;
            width: 28px;
            height: 28px;
            line-height: 28px;
            font-size: 20px;
            border-radius: 8px;
        }
    }
}
:deep(.el-menu) {
    background: none;
}
.el-aside {
    width: auto;
    border-right: 1px solid rgba(194, 204, 217, 0.3);
    background: #fff;
    padding: 0 12px;
    overflow-x: hidden;

    :deep(.el-menu) {
        box-sizing: border-box;
        position: relative;
        border: none;
        .el-menu-item,
        .el-sub-menu {
            .menu-icon {
                transition: all 0.3s linear;
                color: #fff;
                background: #c2c7db;
                border-radius: 8px;
            }
            &.is-active {
                .menu-icon {
                    background: linear-gradient(275deg, #0075ff 31.6%, #00a3ff 96.64%);
                    box-shadow: 1px 4px 6px 0px rgba(0, 102, 255, 0.25);
                    & + span {
                        color: var(--el-color-primary);
                    }
                }
            }
        }
        .el-menu-item:hover {
            // background: none;
            color: var(--el-color-primary);
        }
        &.el-menu--collapse {
            width: 46px;
            // 折叠 一级目录
            .el-menu-item {
                &.is-active {
                    // background: rgba(40, 136, 255, 0.05);
                    color: var(--el-color-primary);
                }
                .el-menu-tooltip__trigger {
                    padding: 0 0 0 12px;
                }
            }
        }
        .el-sub-menu {
            .el-sub-menu__title {
                & > span {
                    font-weight: 600;
                    color: #606266;
                }
            }
            &.is-active {
                // background: rgba(40, 136, 255, 0.05);
                color: var(--el-color-primary);
            }
            &.inside_sub-menu {
                .el-sub-menu__title {
                    font-size: 14px;
                    height: 40px;
                    margin-left: 20px;
                    img {
                        width: 16px;
                    }
                }
                .el-menu .el-menu-item {
                    padding: 0 0 0 22px !important;
                }
            }
            .el-sub-menu__title {
                height: 56px;
                padding: 0 0 0 12px;
                border-radius: 8px;
                font-size: 14px;
                img {
                    width: 20px;
                }
            }
        }
    }

    .el-menu--vertical:not(.el-menu--collapse):not(.el-menu--popup-container) {
        // 一级目录的el-menu-item
        .el-menu-item {
            min-width: 50px;
            height: 56px;
            padding: 0 0 0 12px;
            border-radius: 8px;
            font-size: 14px;
            font-weight: 600;
            color: #606266;
            img {
                width: 20px;
            }
            &.is-active {
                // background: rgba(40, 136, 255, 0.05);
                color: var(--el-color-primary);
            }
        }
        .el-sub-menu {
            // 二级目录的el-menu-item
            .el-menu-item {
                height: 40px;
                margin-left: 20px;
                font-weight: normal;
                color: #8e939c;
                img {
                    width: 16px;
                }
                &.is-active {
                    color: var(--el-color-primary);
                }
            }
            &.inside_sub-menu {
                // 三级目录的el-menu-item
                .el-menu-item {
                    margin-left: 40px;
                }
            }
        }
    }

    .platform-icon {
        width: 28px;
        position: absolute;
        right: 0;
        top: 0;
        cursor: pointer;
        z-index: 10;
    }

    .platform-title {
        width: 180px;
        padding: 26px 10px 0px 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        transition: width 0.5s ease-out;

        .logo {
            width: 34px;
            height: auto;
        }
        .logo-text {
            width: 90px;
            margin-right: 4px;
            transition: width 0.5s ease-out;
        }
        .logo-text-collapse {
            width: 0px;
            margin-right: 0px;
            transition: width 0.5s ease-out;
        }
    }
    .is-collapse {
        width: 34px;
        justify-content: flex-start;
    }
}

.el-container .el-main {
    background-color: rgba(244, 245, 250, 1);
    --el-main-padding: 10px;
}
body .el-popper .user-tooltip {
    .user-menus {
        padding: 6px 0;
        li {
            display: flex;
            align-items: center;
            padding: 6px 12px;
            font-size: 12px;
            color: var(--el-text-color-primary);
            cursor: pointer;
            &:hover {
                background-color: #f5f7f9;
            }
            .user-icon {
                background: #f5f7f9;
                color: var(--el-color-primary);
                font-size: 18px;
                display: inline-block;
                margin-right: 4px;
            }
        }
    }
}
</style>